import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import { FinanceExplanation } from './FinanceExplanation';
import { FinancialAmount } from './FinancialCalculationStatus';
import revenueIcon from '../assets/finance/revenue.svg?no-inline';
import settingsIcon from '../assets/finance/settings.svg?no-inline';
import storehouseReserve from '../assets/finance/storehouse-reserve.webp';

/** Carte claire C-v2. Les graduations sont des valeurs métier ; seules les
 * positions et la longueur du liquide nécessitent des coordonnées dynamiques. */
export function RevenueCard({
  overview,
  onGoal,
  onPeriod,
  selectedPeriod = overview.period,
  periodError,
}: {
  readonly overview: FinancialOverview;
  readonly onGoal: () => void;
  readonly onPeriod?: ((period: string) => void) | undefined;
  readonly selectedPeriod?: string | undefined;
  readonly periodError?: string | null | undefined;
}): React.JSX.Element {
  const goal = overview.monthlyRevenueGoalCents;
  const billed = overview.coverage.salesBilledHtCents ?? overview.coverage.salesKnownHtCents;
  const received = overview.indicators.receivedRevenueHt.cents;
  const receivedComplete = overview.indicators.receivedRevenueHt.confidence === 'confirme';
  const pending = selectedPeriod !== overview.period;
  const point = overview.breakEvenGoalCents ?? null;
  const advised = overview.remuneration?.advisedCents ?? null;
  const distance =
    !receivedComplete || received === null || point === null
      ? null
      : (BigInt(received) - BigInt(point)).toString();
  // Seuls les repères chiffrés peuvent occuper une position sur l'axe.
  // « Super mois » n'a pas de seuil configuré : ni montant fictif ni tiret à 96 %.
  const scaleKnown = goal !== null || point !== null;
  const maximum = Math.max(1, Number(goal ?? 0), Number(point ?? 0)) * 1.25;
  const overflow = scaleKnown && received !== null && Number(received) > maximum;
  const positions = [
    { label: 'POINT ZÉRO', cents: point },
    { label: 'OBJECTIF DE CA', cents: goal },
  ]
    .filter((position): position is { label: string; cents: string } => position.cents !== null)
    .sort((a, b) =>
      BigInt(a.cents) < BigInt(b.cents) ? -1 : BigInt(a.cents) > BigInt(b.cents) ? 1 : 0,
    );
  // Les étiquettes et les traits partagent la même coordonnée métier.
  // Le rabattement sur les bords protège le texte, sans figer son placement.
  const receivedX =
    received === null || !scaleKnown
      ? null
      : Math.max(0, Math.min(100, (Number(received) / maximum) * 100));
  const milestones = positions.map((position) => {
    const x = Math.max(0, Math.min(100, (Number(position.cents) / maximum) * 100));
    return { ...position, x, labelX: x };
  });
  const date = new Date(`${selectedPeriod}-01T12:00:00Z`);
  const month = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  const label = month.charAt(0).toUpperCase() + month.slice(1);
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
  const asOf = today.startsWith(overview.period)
    ? today
    : new Date(Date.UTC(Number(overview.period.slice(0, 4)), Number(overview.period.slice(5)), 0))
        .toISOString()
        .slice(0, 10);
  const formattedDate = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${asOf}T12:00:00Z`));
  const rows = [
    { label: 'Objectif de CA mensuel', cents: goal, large: true },
    {
      label: `${overview.coverage.salesBilledHtCents === null ? 'CA facturé HT connu (sous-total)' : 'CA facturé HT à date'} (${today.startsWith(overview.period) ? 'aujourd’hui, ' : ''}${formattedDate})`,
      cents: billed,
      title:
        overview.coverage.salesBilledHtCents === null
          ? 'Sous-total HT connu des pièces importées ; certaines bases restent absentes ou contradictoires.'
          : 'Pièces HT émises dans le mois, acomptes inclus et avoirs déduits. Le total source du solde est déjà net des acomptes ; ils ne sont pas déduits une seconde fois. Ce ne sont pas des encaissements.',
    },
    {
      label: receivedComplete ? 'CA encaissé à date' : 'CA encaissé HT connu à date (sous-total)',
      cents: received,
      large: true,
      title: overview.indicators.receivedRevenueHt.reason,
    },
    {
      label: 'Point 0 objectivé',
      cents: point,
      large: true,
      title: 'Objectif de point zéro défini dans les réglages de cette carte.',
    },
    {
      label: 'Point zéro conseillé (d’après vos charges)',
      cents: overview.indicators.breakEven.cents,
      title: overview.indicators.breakEven.reason,
    },
    {
      label: 'Distance jusqu’au point 0',
      cents: distance,
      signed: true,
      color:
        distance === null || !receivedComplete
          ? undefined
          : BigInt(distance) >= 0n
            ? 'positive'
            : 'critical',
    },
  ];
  return (
    <article
      className="revenue-card finance-card--controls"
      aria-label={`Chiffre d’affaires : ${month}`}
      aria-busy={pending && !periodError}
    >
      <div className="revenue-card__heading finance-card__heading">
        <h3 className="finance-card__title">
          <img className="finance-card-icon" src={revenueIcon} alt="" />
          CA : {label}
        </h3>
        <div className="finance-card__controls">
          <select
            className="revenue-card__month"
            aria-label="Mois du chiffre d’affaires"
            value={selectedPeriod}
            onChange={(event) => onPeriod?.(event.currentTarget.value)}
          >
            {Array.from(
              { length: (Number(today.slice(0, 4)) - 2020) * 12 + Number(today.slice(5, 7)) - 8 },
              (_, index) => {
                const date = new Date(Date.UTC(2020, 8 + index, 1));
                const period = date.toISOString().slice(0, 7);
                return (
                  <option key={period} value={period}>
                    {new Intl.DateTimeFormat('fr-FR', {
                      month: 'long',
                      year: 'numeric',
                      timeZone: 'UTC',
                    }).format(date)}
                  </option>
                );
              },
            ).reverse()}
          </select>
          <button type="button" aria-label="Configurer l’objectif de CA" onClick={onGoal}>
            <img src={settingsIcon} alt="" />
          </button>
        </div>
      </div>
      {pending ? (
        <p role={periodError ? 'alert' : 'status'}>
          {periodError ?? `Chargement du CA de ${month}…`}
        </p>
      ) : (
        <>
          <p className="revenue-card__intro">Mon CA pour ce mois-ci :</p>
          <div
            className="revenue-card__gauge"
            role="img"
            aria-label={`CA encaissé HT : ${remunerationMoney(received)}, objectif : ${remunerationMoney(goal)}${overflow ? ', au-delà de l’échelle' : ''}`}
          >
            {receivedX !== null ? (
              <span
                className={`revenue-card__received${receivedX >= 90 ? ' revenue-card__received--end' : ''}`}
                style={{ left: `${receivedX}%` }}
              >
                <b>{remunerationMoney(received)}</b>
                <small>CA ENCAISSÉ</small>
                {overflow ? <small className="revenue-card__overflow">↗ Hors échelle</small> : null}
              </span>
            ) : null}
            <div className="revenue-card__rail">
              <i
                style={{
                  width: `${receivedX ?? 0}%`,
                }}
              />
            </div>
            <div className="revenue-card__milestones">
              {milestones.map((position) => (
                <span
                  key={position.label}
                  className="revenue-card__milestone"
                  style={{ left: `${position.labelX}%` }}
                >
                  <b>{remunerationMoney(position.cents)}</b>
                  <small>{position.label}</small>
                </span>
              ))}
              <svg
                className="revenue-card__leaders"
                viewBox="0 0 100 48"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {milestones.map((position) => (
                  <path key={position.label} d={`M ${position.x} 0 V 48`} />
                ))}
              </svg>
            </div>
          </div>
          <dl>
            {rows.map((row) => (
              <div key={row.label} className={row.large ? 'revenue-card__prominent' : undefined}>
                <dt>{row.label}</dt>
                <dd
                  className={row.color ? `revenue-card__${row.color}` : undefined}
                  title={row.title}
                >
                  {row.signed && row.cents !== null && BigInt(row.cents) > 0n ? '+' : ''}
                  <FinancialAmount
                    cents={row.cents}
                    calculation={
                      row.label.startsWith('Point zéro conseillé')
                        ? 'breakEven'
                        : row.label.startsWith('Objectif') || row.label === 'Point 0 objectivé'
                          ? 'settings'
                          : 'sources'
                    }
                    reason={row.title}
                  />
                </dd>
              </div>
            ))}
          </dl>
          <img className="revenue-card__storehouse" src={storehouseReserve} alt="" />
          <FinanceExplanation>
            {overview.breakEvenAnalysis ? (
              <>
                <p>{overview.breakEvenAnalysis.reason}</p>
                <p>
                  Fixes HT : {remunerationMoney(overview.breakEvenAnalysis.fixedHtCents)} ·
                  variables HT : {remunerationMoney(overview.breakEvenAnalysis.variableHtCents)} ·
                  mixtes HT : {remunerationMoney(overview.breakEvenAnalysis.mixedHtCents)} · emprunt
                  à couvrir en trésorerie :{' '}
                  {remunerationMoney(overview.breakEvenAnalysis.financingCents)}.
                </p>
                {overview.breakEvenAnalysis.personalLowerCents != null &&
                overview.breakEvenAnalysis.personalGoalCents != null ? (
                  <p>
                    Intervalle du point zéro personnel :{' '}
                    {remunerationMoney(overview.breakEvenAnalysis.personalLowerCents)} à{' '}
                    {remunerationMoney(overview.breakEvenAnalysis.personalGoalCents)}. Le conseil
                    affiché retient la borne haute.
                  </p>
                ) : null}
              </>
            ) : null}
            {overview.coverage.salesEligibleDocuments > 0 ? (
              <p>
                Facturé : {overview.coverage.salesValuedDocuments}/
                {overview.coverage.salesEligibleDocuments} pièces valorisées. Les pièces dont la
                base HT manque ou se contredit ne sont pas remplacées par leurs paiements.
              </p>
            ) : null}
            {(overview.coverage.salesDateUnknownDocuments ?? 0) > 0 ? (
              <p>
                {overview.coverage.salesDateUnknownDocuments} pièces de vente ont une date
                d’émission non résolue dans les anciennes projections. Le total mensuel reste
                incomplet ; une date de paiement ne devient pas une date d’émission.
              </p>
            ) : null}
            <p>
              Le point zéro réglé ici est votre repère de CA. Le dépasser ne prouve pas que toutes
              vos charges sont couvertes.
            </p>
            {distance !== null &&
            BigInt(distance) >= 0n &&
            overview.salaryGoalCents !== null &&
            advised !== null &&
            BigInt(advised) >= BigInt(overview.salaryGoalCents) ? (
              <p>
                Votre repère de CA est dépassé et le conseil atteint votre objectif de rémunération.
                Consultez sa date et les obligations encore à régler avant un versement.
              </p>
            ) : null}
          </FinanceExplanation>
        </>
      )}
    </article>
  );
}
